import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import Select from "react-select";
import * as APIModels from "../../../../models";
import { DataService } from "../../../../services/DataService";
import Party from "../forms/Party";

type Mode = "party" | "attorney";
type PartyOption = { label: string; value: number };

// Modal for adding a party or an attorney to an existing case.
//
// "Add Party" reuses the new-suit Party form. "Add Attorney" captures a name +
// bar number and lets the user pick which of the case's current parties the
// attorney represents; the attorney is stamped onto each selected party via the
// existing Party.attorney field. Everything updates the submission's parties in
// memory — it persists when the user clicks Submit.
export default function AddPartyAttorneyModal({
    show,
    onClose,
    submission,
    parties,
    onUpdateParties,
}: {
    show: boolean;
    onClose: () => void;
    submission: APIModels.Submission;
    parties: APIModels.Party[];
    onUpdateParties: (parties: APIModels.Party[]) => void;
}) {
    const [mode, setMode] = useState<Mode>("party");
    const [partyTypes, setPartyTypes] = useState([] as APIModels.PartyType[]);
    const [completedParties, setCompletedParties] = useState(parties);

    const [firstName, setFirstName] = useState("");
    const [middleName, setMiddleName] = useState("");
    const [lastName, setLastName] = useState("");
    const [barNumber, setBarNumber] = useState("");
    const [representedParties, setRepresentedParties] = useState<PartyOption[]>(
        []
    );
    const [attorneyError, setAttorneyError] = useState("");

    useEffect(() => {
        setCompletedParties(parties);
    }, [parties]);

    useEffect(() => {
        if (!show) return;
        const caseTypeId =
            submission.caseData?.caseSubTypeID &&
            submission.caseData?.caseSubTypeID !== APIModels.EmptyGuid
                ? submission.caseData.caseSubTypeID
                : submission.caseData?.caseTypeID ?? "";
        DataService.getPartyTypes({ caseTypeId }).then(
            (resp) => setPartyTypes(resp),
            (error) => console.log(error)
        );
    }, [
        show,
        submission.caseData?.caseTypeID,
        submission.caseData?.caseSubTypeID,
    ]);

    const partyName = (p: APIModels.Party): string =>
        p.name && p.name.trim() !== ""
            ? p.name
            : `${p.firstName ?? ""} ${p.middleName ?? ""} ${p.lastName ?? ""}`
                  .replace(/\s+/g, " ")
                  .trim();

    const attorneyName = (p: APIModels.Party): string => {
        if (!p.attorney) return "";
        const name =
            p.attorney.fullName ??
            `${p.attorney.firstName ?? ""} ${p.attorney.lastName ?? ""}`.trim();
        return p.attorney.barNumber
            ? `${name} (Bar ${p.attorney.barNumber})`
            : name;
    };

    const onSaveParty = (party: APIModels.Party) => {
        const updated = [...completedParties, party];
        setCompletedParties(updated);
        onUpdateParties(updated);
    };

    // Parties that can be represented (exclude rows that are themselves attorneys).
    const partyOptions: PartyOption[] = completedParties
        .map((p, i) => ({ p, i }))
        .filter((x) => !x.p.isLeadAttorney)
        .map((x) => ({
            label: `${x.p.partyType ? x.p.partyType + " - " : ""}${partyName(
                x.p
            )}`,
            value: x.i,
        }));

    const resetAttorneyForm = () => {
        setFirstName("");
        setMiddleName("");
        setLastName("");
        setBarNumber("");
        setRepresentedParties([]);
        setAttorneyError("");
    };

    const onSaveAttorney = () => {
        if (!firstName.trim() || !lastName.trim() || !barNumber.trim()) {
            setAttorneyError(
                "First name, last name, and bar number are required."
            );
            return;
        }
        if (representedParties.length === 0) {
            setAttorneyError(
                "Select at least one party the attorney represents."
            );
            return;
        }

        const attorney = {
            firstName: firstName,
            middleName: middleName,
            lastName: lastName,
            fullName: `${firstName} ${lastName}`.trim(),
            barNumber: barNumber,
        };

        const selected = new Set(representedParties.map((o) => o.value));
        const updated = completedParties.map((p, i) =>
            selected.has(i) ? { ...p, attorney: attorney } : p
        );

        setCompletedParties(updated);
        onUpdateParties(updated);
        resetAttorneyForm();
    };

    return (
        <Modal show={show} size="lg" centered onHide={onClose}>
            <Modal.Header>
                <Modal.Title>Add a Party / Attorney</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="btn-group mb-3" role="group">
                    <button
                        type="button"
                        className={`btn ${
                            mode === "party"
                                ? "btn-primary"
                                : "btn-outline-primary"
                        }`}
                        onClick={() => setMode("party")}
                    >
                        Add Party
                    </button>
                    <button
                        type="button"
                        className={`btn ${
                            mode === "attorney"
                                ? "btn-primary"
                                : "btn-outline-primary"
                        }`}
                        onClick={() => setMode("attorney")}
                    >
                        Add Attorney
                    </button>
                </div>

                {completedParties.length > 0 && (
                    <div className="table-responsive mb-3">
                        <table className="table table-sm table-hover mb-0">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Name</th>
                                    <th>Attorney</th>
                                </tr>
                            </thead>
                            <tbody>
                                {completedParties.map((p, i) => (
                                    <tr key={i}>
                                        <td>{p.partyType}</td>
                                        <td>{partyName(p)}</td>
                                        <td>{attorneyName(p)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {mode === "party" ? (
                    <Party
                        attorneyId={submission.attorneyID ?? ""}
                        partyTypes={partyTypes}
                        onSaveParty={onSaveParty}
                        party={{
                            ...new APIModels.Party(),
                            submissionID: submission.id,
                        }}
                        parties={completedParties}
                        currentDocument={null}
                    />
                ) : (
                    <div>
                        <div className="row mb-3">
                            <div className="col-sm-4">
                                <label
                                    htmlFor="txtAttorneyFirstName"
                                    className="form-label"
                                >
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="txtAttorneyFirstName"
                                    placeholder="First Name"
                                    value={firstName}
                                    onChange={(e) =>
                                        setFirstName(e.currentTarget.value)
                                    }
                                />
                            </div>
                            <div className="col-sm-4">
                                <label
                                    htmlFor="txtAttorneyMiddleName"
                                    className="form-label"
                                >
                                    Middle Name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="txtAttorneyMiddleName"
                                    placeholder="Middle Name"
                                    value={middleName}
                                    onChange={(e) =>
                                        setMiddleName(e.currentTarget.value)
                                    }
                                />
                            </div>
                            <div className="col-sm-4">
                                <label
                                    htmlFor="txtAttorneyLastName"
                                    className="form-label"
                                >
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="txtAttorneyLastName"
                                    placeholder="Last Name"
                                    value={lastName}
                                    onChange={(e) =>
                                        setLastName(e.currentTarget.value)
                                    }
                                />
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-sm-4">
                                <label
                                    htmlFor="txtAttorneyBarNumber"
                                    className="form-label"
                                >
                                    Bar Number
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="txtAttorneyBarNumber"
                                    placeholder="Bar Number"
                                    value={barNumber}
                                    onChange={(e) =>
                                        setBarNumber(e.currentTarget.value)
                                    }
                                />
                            </div>
                            <div className="col-sm-8">
                                <label
                                    htmlFor="selRepresentedParties"
                                    className="form-label"
                                >
                                    Representing
                                </label>
                                <Select
                                    id="selRepresentedParties"
                                    name="selRepresentedParties"
                                    isMulti
                                    options={partyOptions}
                                    value={representedParties}
                                    onChange={(value: any) =>
                                        setRepresentedParties(
                                            (value as PartyOption[]) ?? []
                                        )
                                    }
                                    placeholder="Select party(s) this attorney represents"
                                />
                            </div>
                        </div>
                        {attorneyError && (
                            <div
                                className="mb-3"
                                style={{ color: "red", textAlign: "center" }}
                            >
                                {attorneyError}
                            </div>
                        )}
                        <div className="row mb-3">
                            <div className="col-sm-12">
                                <button
                                    className="btn btn-primary"
                                    type="button"
                                    onClick={onSaveAttorney}
                                >
                                    Save Attorney
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}
